import type { PickerOptionAvailabilityState } from './lib/commandAvailability.js'

export type CommandMode = 'edit' | 'view'

export type StepType =
  | 'picker-single'
  | 'canvas-select-single'
  | 'canvas-select-multi'
  | 'canvas-point-multi'
  | 'text-input'

export type StepSelectResult = {
  ok: boolean
  message?: string
  tone?: 'success' | 'error'
  value?: string | string[]
}

export type StepDescriptor = {
  id: string
  label: string
  type: StepType
  prompt: string
  required: boolean
  when?: (ctx: EngineContext) => boolean
  doneLabel?: string
  defaultValue?: string
  allowConnectionHits?: boolean
  options?: Array<{
    label: string
    value: string
    description?: string
    gatedReason?: string
    state?: PickerOptionAvailabilityState
    disabled?: boolean
  }>
  validate?: (value: string | string[]) => { valid: boolean, error?: string }
  normalize?: (value: string) => string
  cardinality?: 'single' | 'multi'
  display?: 'value' | 'count' | 'none'
  keepFeedback?: boolean
  allowCanvasSelection?: boolean
  onSelect?: (value: string, ctx: EngineContext) => StepSelectResult | boolean
  onCanvasSelect?: (value: string, ctx: EngineContext) => StepSelectResult | boolean
  onPoint?: (point: { x: number, y: number }, ctx: EngineContext) => StepSelectResult | boolean
  onHighlight?: (value: string, ctx: EngineContext) => void
  onInput?: (value: string, ctx: EngineContext) => void
  live?: {
    actions: Array<{ id: string, label: string, enabled?: (ctx: EngineContext) => boolean }>
    enterAction: string
    onAction: (actionId: string, ctx: EngineContext) => boolean
  }
  advanceOnSelect?: (value: string, ctx: EngineContext) => boolean
  showDoneWhen?: (ctx: EngineContext) => boolean
}

export type CommandDescriptor = {
  id: string
  label: string
  description: string
  modes?: CommandMode[]
  steps: StepDescriptor[]
  execute: (ctx: EngineContext) => void | Promise<void>
  completeOnExecute?: boolean
  completionUi?: 'close' | 'runner'
}

export type TokenPart = {
  id: string
  kind: 'command' | 'step' | 'action' | 'done'
  label: string
  value?: string
  prefix: string
  active: boolean
  activeTarget?: 'token' | 'value'
}

export type RunnerState = {
  tokens: TokenPart[]
  feedback?: { message: string, tone?: 'success' | 'error' }
  instruction: string
  input?: { tokenId: string, value: string, placeholder?: string }
}

export type EngineContext = {
  getValue: (stepId: string) => string | string[] | undefined
  setValue: (stepId: string, value: string | string[]) => void
  setFeedback: (message?: string, tone?: 'success' | 'error') => void
}

export type EngineEvents = {
  onRunnerUpdate: (state: RunnerState) => void
  onOpenPicker: (opts: {
    label: string
    options: Array<{
      label: string
      value: string
      description?: string
      gatedReason?: string
      state?: PickerOptionAvailabilityState
      disabled?: boolean
    }>
    defaultValue?: string
    onSelect: (value: string) => void
    onConfirm?: () => void
    onHighlight?: (value: string) => void
    closeOnSelect?: boolean
  }) => void
  onClosePicker: () => void
  onEnterCanvasMode: () => void
  onExitCanvasMode: () => void
  onComplete: (opts?: { completionUi?: 'close' | 'runner' }) => void
}

type EngineState = {
  command: CommandDescriptor | null
  stepIndex: number
  values: Record<string, string | string[]>
  feedback?: { message: string, tone?: 'success' | 'error' }
  editingTokenId?: string
  editingValue?: string
  highlightedPickerValue?: string
}

export class CommandEngine {
  private state: EngineState = { command: null, stepIndex: 0, values: {} }
  private events: EngineEvents

  constructor(events: EngineEvents) {
    this.events = events
  }

  enter(command: CommandDescriptor, opts?: { initialValues?: Record<string, string | string[]>, stepIndex?: number }): void {
    this.state = { command, stepIndex: 0, values: {} }
    if (!command.steps.length) {
      this.render()
      const maybePromise = command.execute(this.buildContext())
      const completionUi = command.completionUi ?? 'close'
      if (command.completeOnExecute !== false) {
        if (maybePromise instanceof Promise) {
          void maybePromise.finally(() => {
            this.events.onComplete({ completionUi })
          })
        } else {
          this.events.onComplete({ completionUi })
        }
      }
      return
    }
    if (opts?.initialValues) {
      this.state.values = { ...opts.initialValues }
    }
    command.steps.forEach((step) => {
      if (step.defaultValue !== undefined) {
        this.state.values[step.id] = step.defaultValue
      }
    })
    const targetIndex = opts?.stepIndex !== undefined
      ? Math.min(Math.max(0, opts.stepIndex), command.steps.length - 1)
      : 0
    this.state.stepIndex = this.resolveNextActiveStepIndex(targetIndex)
    this.syncInputSurface()
    this.render()
  }

  exit(): void {
    this.state = { command: null, stepIndex: 0, values: {} }
    this.events.onClosePicker()
    this.events.onExitCanvasMode()
    this.events.onComplete({ completionUi: 'close' })
  }

  // True means an input submission/navigation succeeded and may release focus.
  handleTokenTap(tokenId: string): boolean {
    const step = this.currentStep()
    if (!step || !this.state.command) return false
    if (tokenId === 'done') {
      this.handleDone()
      return false
    }
    if (step.type === 'text-input' && step.live) {
      const action = step.live.actions.find(candidate => candidate.id === tokenId)
      if (action) {
        const ctx = this.buildContext()
        if (action.enabled && !action.enabled(ctx)) return false
        const accepted = step.live.onAction(action.id, ctx)
        this.render()
        return accepted
      }
    }
    if (tokenId !== step.id) return false
    if (step.type === 'picker-single') {
      this.commitPickerSelection()
      return false
    }
    if (step.type === 'text-input') {
      return this.handleEnter()
    }
    return false
  }

  handleEnter(): boolean {
    const step = this.currentStep()
    if (step?.type === 'text-input') {
      return step.live
        ? this.handleTokenTap(step.live.enterAction)
        : this.commitTextInput()
    }
    this.handleDone()
    return false
  }

  handleDone(): void {
    const step = this.currentStep()
    if (!step) return
    if (step.type === 'picker-single') {
      this.commitPickerSelection()
      return
    }
    if (step.type === 'text-input') {
      if (step.live) this.advance()
      else this.commitTextInput()
      return
    }
    if (step.cardinality !== 'multi') return
    const value = this.state.values[step.id]
    if (step.showDoneWhen && !step.showDoneWhen(this.buildContext())) {
      this.setFeedback('select at least one item', 'error')
      this.render()
      return
    }
    if (!Array.isArray(value) || value.length === 0) {
      this.setFeedback('select at least one item', 'error')
      this.render()
      return
    }
    this.advance()
  }

  handlePickerSelect(value: string): void {
    const step = this.currentStep()
    if (!step || step.type !== 'picker-single') return
    const option = this.findPickerOption(step, value)
    const normalized = this.normalizeValue(step, value)
    this.state.highlightedPickerValue = normalized
    if (!option || this.resolvePickerOptionState(option) !== 'armed') {
      this.render()
      return
    }
    this.state.values[step.id] = normalized
    step.onHighlight?.(normalized, this.buildContext())
    if (!step.keepFeedback) {
      this.setFeedback(undefined)
    }
    if (step.advanceOnSelect?.(normalized, this.buildContext())) {
      if (this.applyValue(step, normalized, { persistSingleValue: false })) {
        this.advance()
        return
      }
      this.render()
      return
    }
    this.render()
  }

  handleCanvasSelect(value: string): void {
    const step = this.currentStep()
    if (!step) return
    if (step.type === 'picker-single' && step.allowCanvasSelection && step.onCanvasSelect) {
      if (!this.applyCanvasValue(step, value)) {
        this.render()
        return
      }
      this.render()
      return
    }
    if (step.type !== 'canvas-select-single' && step.type !== 'canvas-select-multi') return
    if (!this.applyValue(step, value)) {
      this.render()
      return
    }
    if (step.cardinality === 'multi') {
      this.render()
      return
    }
    this.advance()
  }

  handleCanvasPoint(point: { x: number, y: number }): void {
    const step = this.currentStep()
    if (!step || step.type !== 'canvas-point-multi') return
    if (!this.applyPoint(step, point)) {
      this.render()
      return
    }
    this.render()
  }

  handleTextInput(value: string): void {
    const step = this.currentStep()
    if (!step || step.type !== 'text-input') return
    const normalized = this.normalizeValue(step, value)
    this.state.editingTokenId = step.id
    this.state.editingValue = normalized
    if (step.onInput) {
      step.onInput(normalized, this.buildContext())
      this.render()
    }
  }

  refreshLiveInput(): void {
    const step = this.currentStep()
    if (!step || step.type !== 'text-input' || !step.live) return
    step.onInput?.(this.state.editingValue ?? '', this.buildContext())
    this.render()
  }

  private currentStep(): StepDescriptor | undefined {
    return this.getStepAtIndex(this.state.stepIndex)
  }

  allowsConnectionHits(): boolean {
    const step = this.currentStep()
    return Boolean(step && (step.type === 'canvas-select-single' || step.type === 'canvas-select-multi') && step.allowConnectionHits)
  }

  isCanvasPointMode(): boolean {
    const step = this.currentStep()
    return Boolean(step && step.type === 'canvas-point-multi')
  }

  private commitTextInput(): boolean {
    const step = this.currentStep()
    if (!step || step.type !== 'text-input') return false
    const value = this.state.editingValue ?? ''
    const validation = this.validateValue(step, value)
    if (!validation.valid) {
      this.setFeedback(validation.error ?? 'invalid input', 'error')
      this.render()
      return false
    }
    this.state.values[step.id] = value
    this.state.editingTokenId = undefined
    this.state.editingValue = undefined
    this.setFeedback(undefined)
    this.advance()
    return true
  }

  private commitPickerSelection(): void {
    const step = this.currentStep()
    if (!step || step.type !== 'picker-single') return
    const current = this.state.values[step.id]
    if (typeof current !== 'string' || current.trim().length === 0) {
      this.setFeedback('pick an option', 'error')
      this.render()
      return
    }
    const option = this.findPickerOption(step, current)
    if (!option || this.resolvePickerOptionState(option) !== 'armed') {
      this.render()
      return
    }
    if (this.applyValue(step, current, { persistSingleValue: false })) {
      this.advance()
      return
    }
    this.render()
  }

  private applyValue(step: StepDescriptor, rawValue: string, opts?: { persistSingleValue?: boolean }): boolean {
    const normalizedValue = this.normalizeValue(step, rawValue)
    let value: string | string[] = normalizedValue
    if (step.onSelect) {
      const result = step.onSelect(value, this.buildContext())
      if (result === false || (typeof result === 'object' && !result.ok)) {
        if (typeof result === 'object' && result.message) {
          this.setFeedback(result.message, result.tone ?? 'error')
        }
        return false
      }
      if (typeof result === 'object' && typeof result.value !== 'undefined') {
        value = result.value
      }
    }
    const validation = this.validateValue(step, value)
    if (!validation.valid) {
      this.setFeedback(validation.error ?? 'invalid input', 'error')
      return false
    }
    if (step.cardinality === 'multi') {
      if (Array.isArray(value)) {
        this.state.values[step.id] = [...value]
      } else {
        const existing = this.state.values[step.id]
        const list = Array.isArray(existing) ? [...existing] : []
        if (!list.includes(value)) {
          list.push(value)
        }
        this.state.values[step.id] = list
      }
    } else if (opts?.persistSingleValue !== false) {
      this.state.values[step.id] = value
    }
    if (!step.keepFeedback) {
      this.setFeedback(undefined)
    }
    return true
  }

  private applyCanvasValue(step: StepDescriptor, rawValue: string): boolean {
    if (!step.onCanvasSelect) return false
    const normalizedValue = this.normalizeValue(step, rawValue)
    const result = step.onCanvasSelect(normalizedValue, this.buildContext())
    if (result === false || (typeof result === 'object' && !result.ok)) {
      if (typeof result === 'object' && result.message) {
        this.setFeedback(result.message, result.tone ?? 'error')
      }
      return false
    }
    if (!step.keepFeedback) {
      this.setFeedback(undefined)
    }
    return true
  }

  private applyPoint(step: StepDescriptor, point: { x: number, y: number }): boolean {
    if (!step.onPoint) return false
    let value: string | string[] = []
    const result = step.onPoint(point, this.buildContext())
    if (result === false || (typeof result === 'object' && !result.ok)) {
      if (typeof result === 'object' && result.message) {
        this.setFeedback(result.message, result.tone ?? 'error')
      }
      return false
    }
    if (typeof result === 'object' && typeof result.value !== 'undefined') {
      value = result.value
    }
    const validation = this.validateValue(step, value)
    if (!validation.valid) {
      this.setFeedback(validation.error ?? 'invalid input', 'error')
      return false
    }
    if (step.cardinality === 'multi') {
      if (Array.isArray(value)) {
        this.state.values[step.id] = [...value]
      } else {
        const existing = this.state.values[step.id]
        const list = Array.isArray(existing) ? [...existing] : []
        if (!list.includes(value)) {
          list.push(value)
        }
        this.state.values[step.id] = list
      }
    } else {
      this.state.values[step.id] = value
    }
    if (!step.keepFeedback) {
      this.setFeedback(undefined)
    }
    return true
  }

  private normalizeValue(step: StepDescriptor, value: string): string {
    if (step.normalize) return step.normalize(value)
    return value.trim()
  }

  private validateValue(step: StepDescriptor, value: string | string[]): { valid: boolean, error?: string } {
    if (step.validate) return step.validate(value)
    return { valid: true }
  }

  private advance(): void {
    const command = this.state.command
    if (!command) return
    const nextIndex = this.resolveNextActiveStepIndex(this.state.stepIndex + 1)
    if (nextIndex >= command.steps.length) {
      void command.execute(this.buildContext())
      const completionUi = command.completionUi ?? 'close'
      if (command.completeOnExecute !== false) {
        this.events.onComplete({ completionUi })
        return
      }
      this.render()
      return
    }
    this.state.stepIndex = nextIndex
    this.state.editingTokenId = undefined
    this.state.editingValue = undefined
    this.state.highlightedPickerValue = undefined
    this.syncInputSurface()
    this.render()
  }

  private resolvePickerOptionState(option: { state?: PickerOptionAvailabilityState, disabled?: boolean }): PickerOptionAvailabilityState {
    if (option.state) return option.state
    return option.disabled ? 'disabled' : 'armed'
  }

  private findPickerOption(
    step: StepDescriptor,
    rawValue?: string
  ): NonNullable<StepDescriptor['options']>[number] | undefined {
    if (!rawValue) return undefined
    const normalized = this.normalizeValue(step, rawValue)
    return (step.options ?? []).find(option => this.normalizeValue(step, option.value) === normalized)
  }

  private resolveHighlightedPickerOption(step: StepDescriptor): NonNullable<StepDescriptor['options']>[number] | undefined {
    const highlighted = this.state.highlightedPickerValue
    if (typeof highlighted === 'string' && highlighted.length > 0) {
      const option = this.findPickerOption(step, highlighted)
      if (option) return option
    }
    const current = this.state.values[step.id]
    if (typeof current === 'string' && current.length > 0) {
      return this.findPickerOption(step, current)
    }
    return undefined
  }

  private syncInputSurface(): void {
    const step = this.currentStep()
    if (!step) return
    if (step.type === 'picker-single') {
      const options = step.options ?? []
      if (options.length === 1) {
        const single = this.normalizeValue(step, options[0].value)
        if (typeof this.state.values[step.id] !== 'string') {
          this.state.values[step.id] = single
        }
        this.state.highlightedPickerValue = single
        if (step.onHighlight) {
          step.onHighlight(single, this.buildContext())
        }
        this.events.onClosePicker()
        this.events.onExitCanvasMode()
        return
      }
      this.events.onOpenPicker({
        label: step.label,
        options,
        defaultValue: typeof this.state.values[step.id] === 'string' ? this.state.values[step.id] as string : undefined,
        onSelect: (value) => this.handlePickerSelect(value),
        onConfirm: () => this.handleTokenTap(step.id),
        onHighlight: (value) => {
          const normalized = this.normalizeValue(step, value)
          this.state.highlightedPickerValue = normalized
          step.onHighlight?.(normalized, this.buildContext())
          this.render()
        },
        closeOnSelect: false
      })
      if (step.allowCanvasSelection) {
        this.events.onEnterCanvasMode()
      } else {
        this.events.onExitCanvasMode()
      }
      return
    }
    if (step.type === 'canvas-select-single' || step.type === 'canvas-select-multi' || step.type === 'canvas-point-multi') {
      this.events.onClosePicker()
      this.events.onEnterCanvasMode()
      return
    }
    if (step.type === 'text-input') {
      this.state.editingTokenId = step.id
      this.state.editingValue = typeof this.state.values[step.id] === 'string' ? this.state.values[step.id] as string : ''
      this.state.highlightedPickerValue = undefined
      this.events.onClosePicker()
      this.events.onExitCanvasMode()
      step.onInput?.(this.state.editingValue, this.buildContext())
      return
    }
    this.events.onClosePicker()
    this.events.onExitCanvasMode()
  }

  private setFeedback(message?: string, tone?: 'success' | 'error'): void {
    if (!message) {
      this.state.feedback = undefined
      return
    }
    this.state.feedback = { message, tone }
  }

  private buildRunnerState(): RunnerState {
    const command = this.state.command
    if (!command) {
      return { tokens: [], instruction: '' }
    }
    const tokens: TokenPart[] = []
    tokens.push({
      id: command.id,
      kind: 'command',
      label: command.label,
      prefix: '',
      active: false
    })
    const current = this.currentStep()
    command.steps.forEach((step, idx) => {
      if (!this.isStepActiveInFlow(step)) return
      if (idx > this.state.stepIndex) return
      const isActive = idx === this.state.stepIndex
      const options = step.options ?? []
      const hideSinglePickerStepToken =
        isActive
        && step.type === 'picker-single'
        && options.length === 1
        && (step.display === 'none' || Boolean(step.doneLabel))
      let value: string | undefined
      const raw = this.state.values[step.id]
      const display = step.display ?? ((step.type === 'canvas-select-single' || step.type === 'canvas-select-multi' || step.type === 'canvas-point-multi') ? 'none' : (step.cardinality === 'multi' ? 'none' : 'value'))
      if (Array.isArray(raw)) {
        if (display === 'count') {
          value = String(raw.length)
        } else if (display === 'value') {
          value = raw.join(',')
        }
      } else if (typeof raw === 'string') {
        if (display !== 'none') {
          value = raw
        }
      }
      if (this.state.editingTokenId === step.id && typeof this.state.editingValue === 'string') {
        value = this.state.editingValue
      }
      if (!hideSinglePickerStepToken) {
        tokens.push({
          id: step.id,
          kind: 'step',
          label: step.label,
          value,
          prefix: '/',
          active: isActive,
          activeTarget: isActive && step.type === 'picker-single' && value ? 'value' : 'token'
        })
      }
      if (isActive && step.type === 'text-input') {
        step.live?.actions.forEach(action => {
          tokens.push({
            id: action.id,
            kind: 'action',
            label: action.label,
            prefix: '',
            active: action.enabled ? action.enabled(this.buildContext()) : true,
            activeTarget: 'token'
          })
        })
        tokens.push({
          id: 'done',
          kind: 'done',
          label: step.doneLabel ?? 'done',
          prefix: '|',
          active: true,
          activeTarget: 'token'
        })
      } else if (isActive && step.type === 'picker-single') {
        const currentValue = this.state.values[step.id]
        const currentOption = typeof currentValue === 'string'
          ? this.findPickerOption(step, currentValue)
          : undefined
        const hasReadySingleValue = options.length === 1
          && typeof currentValue === 'string'
          && currentValue.trim().length > 0
          && (!currentOption || this.resolvePickerOptionState(currentOption) === 'armed')
        const shouldShowDone = hasReadySingleValue || Boolean(step.showDoneWhen?.(this.buildContext()))
        if (shouldShowDone) {
          tokens.push({
            id: 'done',
            kind: 'done',
            label: step.doneLabel ?? 'done',
            prefix: '|',
            active: true,
            activeTarget: 'token'
          })
        }
      } else if (isActive && step.cardinality === 'multi') {
        const list = this.state.values[step.id]
        const shouldShowDone = Array.isArray(list)
          && list.length > 0
          && (step.showDoneWhen ? step.showDoneWhen(this.buildContext()) : true)
        if (shouldShowDone) {
          tokens.push({
            id: 'done',
            kind: 'done',
            label: 'done',
            prefix: '|',
            active: true,
            activeTarget: 'token'
          })
        }
      }
    })
    let instruction = current?.prompt ?? ''
    if (current && current.type === 'picker-single') {
      const option = this.resolveHighlightedPickerOption(current)
      if (option) {
        const optionState = this.resolvePickerOptionState(option)
        instruction = optionState === 'gated'
          ? (option.gatedReason ?? option.description ?? current.prompt)
          : (option.description ?? current.prompt)
      }
    }
    const input = this.state.editingTokenId
      ? { tokenId: this.state.editingTokenId, value: this.state.editingValue ?? '' }
      : undefined
    return {
      tokens,
      feedback: this.state.feedback,
      instruction,
      input
    }
  }

  private render(): void {
    this.events.onRunnerUpdate(this.buildRunnerState())
  }

  private isStepActiveInFlow(step: StepDescriptor): boolean {
    return step.when ? step.when(this.buildContext()) : true
  }

  private getStepAtIndex(index: number): StepDescriptor | undefined {
    const command = this.state.command
    if (!command) return undefined
    const step = command.steps[index]
    if (!step || !this.isStepActiveInFlow(step)) return undefined
    return step
  }

  private resolveNextActiveStepIndex(startIndex: number): number {
    const command = this.state.command
    if (!command) return startIndex
    for (let index = startIndex; index < command.steps.length; index += 1) {
      if (this.isStepActiveInFlow(command.steps[index])) return index
    }
    return command.steps.length
  }

  private buildContext(): EngineContext {
    return {
      getValue: (stepId) => this.state.values[stepId],
      setValue: (stepId, value) => {
        this.state.values[stepId] = value
      },
      setFeedback: (message, tone) => {
        this.setFeedback(message, tone)
        this.render()
      }
    }
  }
}
